import { Link } from 'react-router-dom';
import { Clock, MapPin, ChevronRight } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { ChannelBadge } from './ChannelBadge';

export const MatchCard = ({ match, featured = false }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  };

  const isLive = match.status === 'live';
  const isFinished = match.status === 'finished';

  return (
    <Link 
      to={`/jogo/${match.id}`}
      className={`block bg-card-dark border border-zinc-800 hover:border-neon-green/50 transition-all duration-300 group relative overflow-hidden ${featured ? 'col-span-full' : ''}`}
      data-testid={`match-card-${match.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800/50 bg-pitch-black/50">
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
          {match.league}
        </span>
        {isLive ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-live-red text-white text-xs font-bold uppercase tracking-wider animate-pulse-live">
            <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
            AO VIVO
          </span>
        ) : isFinished ? (
          <span className="text-xs font-medium text-zinc-500 uppercase">Encerrado</span>
        ) : (
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Clock className="w-3 h-3" />
            <span>{getDayName(match.match_date)} {formatDate(match.match_date)} - {match.match_time}</span>
          </div>
        )}
      </div>

      {/* Teams */}
      <div className="px-4 py-6">
        <div className="flex items-center justify-between gap-4">
          {/* Home Team */}
          <div className="flex-1 flex items-center gap-3">
            {match.team_home_logo && (
              <img 
                src={match.team_home_logo} 
                alt={match.team_home}
                className="w-12 h-12 md:w-16 md:h-16 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <span className="font-heading text-lg md:text-2xl font-bold uppercase tracking-tight">
              {match.team_home}
            </span>
          </div>

          {/* Score / VS */}
          <div className="flex-shrink-0 text-center px-4">
            {isFinished || isLive ? (
              <div className="font-mono text-3xl md:text-4xl font-bold tracking-wider">
                <span className={match.home_score > match.away_score ? 'text-neon-green' : 'text-white'}>
                  {match.home_score ?? 0}
                </span>
                <span className="text-zinc-600 mx-2">-</span>
                <span className={match.away_score > match.home_score ? 'text-neon-green' : 'text-white'}>
                  {match.away_score ?? 0}
                </span>
              </div>
            ) : (
              <span className="font-heading text-2xl md:text-3xl font-bold text-zinc-600">VS</span>
            )}
          </div>

          {/* Away Team */}
          <div className="flex-1 flex items-center justify-end gap-3">
            <span className="font-heading text-lg md:text-2xl font-bold uppercase tracking-tight text-right">
              {match.team_away}
            </span>
            {match.team_away_logo && (
              <img 
                src={match.team_away_logo} 
                alt={match.team_away}
                className="w-12 h-12 md:w-16 md:h-16 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </div>
        </div>

        {/* Countdown for featured */}
        {featured && !isFinished && !isLive && (
          <div className="mt-6 pt-6 border-t border-zinc-800/50 flex justify-center">
            <CountdownTimer targetDate={match.match_date} targetTime={match.match_time} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-zinc-800/50 bg-pitch-black/30 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <MapPin className="w-3 h-3" />
          <span>{match.stadium}</span>
        </div>
        
        {match.channels && match.channels.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {match.channels.slice(0, 3).map((channel, idx) => (
              <ChannelBadge key={idx} channel={channel} />
            ))}
          </div>
        )}

        <div className="flex items-center text-neon-green text-xs font-bold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
          Ver detalhes
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
};

export default MatchCard;
